/**
 * Order adapter
 * Converts Storefront API Order to Analyzify format
 */

import type { StorefrontOrder, StorefrontOrderLineItem } from '../types/storefront';
import type { AnalyzifyOrder, AnalyzifyOrderItem } from '../types/products';
import { extractNumericId } from './ids';

/**
 * Convert money string to cents
 * Defensively handles null/undefined/NaN amounts
 */
function moneyToCents(amount: string | null | undefined): number {
  if (!amount) return 0;
  const parsed = parseFloat(amount);
  return isNaN(parsed) ? 0 : Math.round(parsed * 100);
}

/**
 * Create empty order item for null inputs
 */
function createEmptyOrderItem(): AnalyzifyOrderItem {
  return {
    product_id: 0,
    variant_id: 0,
    title: '',
    quantity: 0,
    price: 0,
    sku: '',
    vendor: '',
    product_type: '',
  };
}

/**
 * Adapt an order line item to Analyzify format
 * Defensively handles null/undefined line item data
 */
export function adaptOrderItem(lineItem: StorefrontOrderLineItem | null | undefined): AnalyzifyOrderItem {
  // Return safe default for null/undefined line item
  if (!lineItem) {
    return createEmptyOrderItem();
  }

  const variant = lineItem.variant;

  return {
    product_id: variant ? extractNumericId(variant.product?.id) : 0,
    variant_id: variant ? extractNumericId(variant.id) : 0,
    title: lineItem.title || '',
    quantity: lineItem.quantity || 0,
    price: variant ? moneyToCents(variant.price?.amount) : 0,
    sku: variant?.sku || '',
    vendor: variant?.product?.vendor || '',
    product_type: variant?.product?.productType || '',
  };
}

/**
 * Create an empty order object
 */
export function createEmptyOrder(currency: string = 'USD'): AnalyzifyOrder {
  return {
    id: '0',
    order_number: 0,
    total_price: 0,
    total_tax: 0,
    total_shipping: 0,
    currency,
    items: [],
  };
}

/**
 * Adapt a Storefront API order to Analyzify format
 * Defensively handles null/undefined order data
 */
export function adaptOrder(order: StorefrontOrder | null | undefined): AnalyzifyOrder {
  // Return empty order for null/undefined
  if (!order) {
    return createEmptyOrder();
  }

  // Safely get line item edges
  const lineItemEdges = order.lineItems?.edges || [];
  const items = lineItemEdges.map((edge) => adaptOrderItem(edge?.node));

  return {
    id: extractNumericId(order.id).toString(),
    order_number: order.orderNumber || 0,
    total_price: moneyToCents(order.totalPrice?.amount),
    total_tax: moneyToCents(order.totalTax?.amount),
    total_shipping: moneyToCents(order.totalShippingPrice?.amount),
    currency: order.currencyCode || 'USD',
    items,
  };
}
